import { Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { ActiveOrganizationContextHeader } from "src/components/ActiveOrganizationContextHeader";

type SimpleLayoutProps = {
    children: ReactNode;
    title: string;
    subtitle: string;
    selectors?: ReactNode;
};
export const SimpleLayout = (props: SimpleLayoutProps) => {
    const { children, title, selectors } = props;
    const theme = useTheme();

    return (
        <Stack>
            {/* Header with title and selectors */}
            <Box sx={{ background: { xs: "white", md: "transparent" } }}>
                <Stack
                    padding={{ xs: theme.spacing(2, 2), lg: theme.spacing(3, 6) }}
                    gap={{ xs: theme.spacing(1), lg: theme.spacing(2) }}
                >
                    {/* First row: Title + Subtitle (desktop) / Title only (mobile) */}
                    <Stack
                        flexDirection={"row"}
                        justifyContent={{ xs: "space-between", lg: "space-between" }}
                        alignItems={"center"}
                        gap={theme.spacing(2)}
                        minWidth={0}
                    >
                        <Stack
                            flexDirection={"row"}
                            justifyContent={{ xs: "center", lg: "flex-start" }}
                            alignItems={"center"}
                            gap={theme.spacing(2)}
                            flex={{ xs: 1, lg: "auto" }}
                            minWidth={0}
                            width={{ xs: "100%", lg: "auto" }}
                            sx={{
                                paddingLeft: { xs: 0, lg: 0 },
                            }}
                        >
                            <ActiveOrganizationContextHeader
                                sx={{
                                    width: "100%",
                                    flexDirection: { xs: "column", md: "row" },
                                    justifyContent: { xs: "center", lg: "flex-start" },
                                    gap: { xs: theme.spacing(0.5), md: theme.spacing(2) },
                                }}
                                titleSx={{
                                    width: { xs: "100%", md: "auto" },
                                    justifyContent: { xs: "center", lg: "flex-start" },
                                }}
                                contextRowSx={{
                                    maxWidth: "100%",
                                    justifyContent: "center",
                                    "& .MuiDivider-root": { display: { xs: "none", md: "block" } },
                                }}
                                contextSx={{ maxWidth: "100%" }}
                                labelSx={{
                                    maxWidth: "100%",
                                    whiteSpace: "normal",
                                    overflow: "visible",
                                    textOverflow: "clip",
                                    overflowWrap: "anywhere",
                                    textAlign: { xs: "center", md: "left" },
                                }}
                                title={
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            textAlign: { xs: "center", lg: "left" },
                                            width: { xs: "100%", lg: "auto" },
                                            lineHeight: 1.2,
                                            margin: 0,
                                            padding: 0,
                                            maxWidth: "100%",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                }
                            />
                        </Stack>

                        {/* Selectors - Only shown on desktop/tablet in this row */}
                        <Stack
                            direction={{ xs: "column", lg: "row" }}
                            justifyContent="center"
                            alignItems={{ xs: "flex-end", lg: "center" }}
                            gap={theme.spacing(1)}
                            minWidth={0}
                            maxWidth="100%"
                            sx={{ display: { xs: "none", md: "flex" } }}
                        >
                            {selectors}
                        </Stack>
                    </Stack>

                    {/* Second row: Selectors (mobile only) */}
                    <Stack
                        flexDirection={"row"}
                        justifyContent={"flex-end"}
                        alignItems={"center"}
                        gap={theme.spacing(1)}
                        minWidth={0}
                        maxWidth="100%"
                        sx={{ display: { xs: "flex", md: "none" } }}
                    >
                        {selectors}
                    </Stack>
                </Stack>
            </Box>

            {/* Content */}
            <Stack padding={{ xs: theme.spacing(2, 2), lg: theme.spacing(2, 6) }}>{children}</Stack>
        </Stack>
    );
};
